import { useEffect, useRef, useState } from 'react';
import type { Conversation, VavClient } from '@vav/client-core';
import { Avatar } from './Avatar.tsx';
import { useT } from '../i18n.tsx';
import { AvatarCropDialog } from './AvatarCropDialog.tsx';

/**
 * Edit a group's name and picture.
 *
 * Both travel through the same sealed group-update that named the group at
 * creation, so the server sees neither. Any member can edit — the sealed
 * design cannot enforce a creator-only rule server-side, and pretending
 * otherwise in the UI would be a lock on a door with no wall.
 */
export function GroupDialog({
  client,
  conversation,
  onClose,
}: {
  client: VavClient;
  conversation: Conversation;
  onClose: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(conversation.title ?? '');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  async function leave() {
    setBusy(true);
    try {
      await client.leaveGroup(conversation.id);
      onClose();
    } catch {
      setFailed(true);
      setBusy(false);
    }
  }
  const fileInput = useRef<HTMLInputElement>(null);
  const [cropFile, setCropFile] = useState<File>();
  const [cropped, setCropped] = useState<{ data: Uint8Array; meta: { mime: string; name: string } }>();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save() {
    setBusy(true);
    setFailed(false);
    try {
      await client.updateGroup(conversation.id, {
        name: name.trim() !== (conversation.title ?? '') ? name : undefined,
        avatar: cropped,
      });
      onClose();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="card dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('group.edit.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dialog__header">
          <h2>{t('group.edit.title')}</h2>
          <button className="icon" onClick={onClose} aria-label={t('profile.name.cancel')}>
            ✕
          </button>
        </header>

        <div className="profile__identity">
          <button
            className="profile__avatarButton"
            onClick={() => fileInput.current?.click()}
            aria-label={t('profile.avatar.change')}
            title={t('profile.avatar.change')}
          >
            <Avatar
              name={conversation.title ?? t('sidebar.group')}
              size={56}
              group
              avatar={conversation.avatar}
              client={client}
            />
            <span className="profile__avatarBadge" aria-hidden="true">📷</span>
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setCropFile(file);
              e.target.value = '';
            }}
          />
          {cropped && <span className="muted">✓</span>}
          {cropFile && (
            <AvatarCropDialog
              file={cropFile}
              onCancel={() => setCropFile(undefined)}
              onDone={(data, meta) => {
                setCropped({ data, meta });
                setCropFile(undefined);
              }}
            />
          )}
        </div>

        <label htmlFor="group-name">{t('group.edit.name')}</label>
        <input
          id="group-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={64}
          dir="auto"
        />

        {failed && <span className="error">{t('profile.name.failed')}</span>}
        <button className="primary" disabled={busy} onClick={() => void save()}>
          {t('profile.name.save')}
        </button>

        {/* Leaving tells the members and the server, then forgets the group
            here — the exit Chen asked for. "Delete the group for everyone"
            deliberately does not exist: the sealed design has no owner the
            server could verify, so anyone could erase everyone's group. */}
        {!confirmLeave ? (
          <button className="danger" onClick={() => setConfirmLeave(true)}>
            🚪 {t('group.leave')}
          </button>
        ) : (
          <div className="profile__deleteConfirm">
            <p className="hint">{t('group.leave.explain')}</p>
            <span className="profile__deleteActions">
              <button className="danger" disabled={busy} onClick={() => void leave()}>
                {t('group.leave')}
              </button>
              <button onClick={() => setConfirmLeave(false)}>{t('profile.name.cancel')}</button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
